// components/editorial/index.tsx
// ─── Editorial primitives for Kursi (non-KURSI-LEARN surfaces) ──────────────
// Aesthetic: warm cream paper, ink black, terracotta accent, serif display.
// All primitives are RTL-first (Hebrew). Avoid wrapping these in <Text>; they
// already render text or accept children appropriately.
// ─────────────────────────────────────────────────────────────────────────────

import { ReactNode, forwardRef, useMemo } from 'react';
import {
  View, Text, Pressable, ScrollView, StyleSheet, Platform, TextInput,
  TextProps, PressableProps, ViewProps, useWindowDimensions, type StyleProp, type ViewStyle, type TextStyle,
} from 'react-native';
import { theme, INK, INK_SOFT, INK_MUTED, INK_FAINT, PAPER, PAPER_SOFT, ACCENT, ACCENT_DEEP, RULE, CRIMSON, FOREST, GOLD } from '@/constants/theme';

const IS_WEB = Platform.OS === 'web';
const SERIF = theme.fonts.serif;
const SERIF_BOLD = theme.fonts.serifBold;
const SERIF_BLACK = theme.fonts.serifBlack;
const SERIF_MED = theme.fonts.serifMedium;
const SANS = theme.fonts.regular;
const SANS_MED = theme.fonts.medium;
const SANS_SEMI = theme.fonts.semiBold;
const SANS_BOLD = theme.fonts.bold;
const MONO = theme.fonts.mono;

// ─── Paper: page background ─────────────────────────────────────────────────
export function Paper({ children, tone = 'paper', style, ...rest }: ViewProps & { tone?: 'paper' | 'soft' | 'deep' | 'ink'; children?: ReactNode }) {
  const bg = tone === 'soft' ? PAPER_SOFT : tone === 'deep' ? '#E8DFCB' : tone === 'ink' ? INK : PAPER;
  return (
    <View style={[{ flex: 1, backgroundColor: bg }, style]} {...rest}>
      {children}
    </View>
  );
}

// ─── PaperScroll: scroll view with paper background ─────────────────────────
export const PaperScroll = forwardRef<ScrollView, any>(function PaperScroll(
  { children, contentContainerStyle, tone = 'paper', ...rest }: any,
  ref,
) {
  const bg = tone === 'soft' ? PAPER_SOFT : tone === 'ink' ? INK : PAPER;
  return (
    <ScrollView
      ref={ref}
      style={[{ flex: 1, backgroundColor: bg }, rest.style]}
      contentContainerStyle={[{ backgroundColor: bg }, contentContainerStyle]}
      {...rest}
    >
      {children}
    </ScrollView>
  );
});

// ─── Section: max-width content wrapper ─────────────────────────────────────
export function Section({
  children, max = 1160, py = 'lg', px = 'md', style, ...rest
}: ViewProps & { children?: ReactNode; max?: number; py?: keyof typeof theme.spacing; px?: keyof typeof theme.spacing }) {
  return (
    <View
      style={[{
        width: '100%',
        maxWidth: max,
        alignSelf: 'center',
        paddingHorizontal: theme.spacing[px],
        paddingVertical: theme.spacing[py],
      }, style]}
      {...rest}
    >
      {children}
    </View>
  );
}

// ─── Rule: editorial horizontal/vertical rule ───────────────────────────────
export function Rule({
  weight = 1, color = RULE, vertical = false, gap = 0, style,
}: { weight?: number; color?: string; vertical?: boolean; gap?: number; style?: StyleProp<ViewStyle> }) {
  return (
    <View
      style={[
        vertical
          ? { width: weight, alignSelf: 'stretch', backgroundColor: color, marginHorizontal: gap }
          : { height: weight, width: '100%', backgroundColor: color, marginVertical: gap },
        style,
      ]}
    />
  );
}

// ─── DoubleRule: classic editorial double-line ─────────────────────────────
export function DoubleRule({ color = RULE, style }: { color?: string; style?: StyleProp<ViewStyle> }) {
  return (
    <View style={style}>
      <View style={{ height: 2, backgroundColor: color }} />
      <View style={{ height: 4 }} />
      <View style={{ height: 1, backgroundColor: color }} />
    </View>
  );
}

// ─── Typography primitives ──────────────────────────────────────────────────
type TextLike = TextProps & { children?: ReactNode; align?: 'right' | 'left' | 'center' | 'auto'; color?: string };

const baseTextProps = (p: any) => ({
  ...(IS_WEB ? { 'aria-level': p.level } : {}),
  ...(IS_WEB && p.level ? { role: 'heading' as const } : {}),
});

export function Display({ children, style, color = INK, align = 'right', level = 1, ...rest }: TextLike & { level?: number }) {
  return (
    <Text
      style={[styles.display, { color, textAlign: align as any }, style]}
      {...baseTextProps({ level })}
      {...rest}
    >
      {children}
    </Text>
  );
}
export function Headline({ children, style, color = INK, align = 'right', level = 2, ...rest }: TextLike & { level?: number }) {
  return (
    <Text style={[styles.headline, { color, textAlign: align as any }, style]} {...baseTextProps({ level })} {...rest}>
      {children}
    </Text>
  );
}
export function Title({ children, style, color = INK, align = 'right', level = 2, ...rest }: TextLike & { level?: number }) {
  return (
    <Text style={[styles.title, { color, textAlign: align as any }, style]} {...baseTextProps({ level })} {...rest}>
      {children}
    </Text>
  );
}
export function SectionTitle({ children, style, color = INK, align = 'right', level = 2, ...rest }: TextLike & { level?: number }) {
  return (
    <Text style={[styles.sectionTitle, { color, textAlign: align as any }, style]} {...baseTextProps({ level })} {...rest}>
      {children}
    </Text>
  );
}
export function Body({ children, style, color = INK_SOFT, align = 'right', size = 'body', ...rest }: TextLike & { size?: 'body' | 'bodyLg' | 'small' }) {
  return (
    <Text style={[styles[size], { color, textAlign: align as any }, style]} {...rest}>
      {children}
    </Text>
  );
}
export function Caption({ children, style, color = INK_MUTED, align = 'right', ...rest }: TextLike) {
  return <Text style={[styles.caption, { color, textAlign: align as any }, style]} {...rest}>{children}</Text>;
}
export function Kicker({ children, style, color = ACCENT, align = 'right', ...rest }: TextLike) {
  return <Text style={[styles.kicker, { color, textAlign: align as any }, style]} {...rest}>{children}</Text>;
}
export function Numeral({ children, style, color = INK, size = 96, ...rest }: TextLike & { size?: number }) {
  return (
    <Text
      style={[{
        fontFamily: SERIF_BLACK,
        fontSize: size,
        lineHeight: size,
        color,
        letterSpacing: -size * 0.04,
      }, style]}
      {...rest}
    >
      {children}
    </Text>
  );
}

// ─── EditorialButton ─────────────────────────────────────────────────────────
export function EditorialButton({
  label, onPress, variant = 'primary', size = 'md', icon, trailing, fullWidth, style, textStyle, disabled, ...rest
}: {
  label: string;
  onPress?: () => void;
  variant?: 'primary' | 'secondary' | 'ghost' | 'ink' | 'accent';
  size?: 'sm' | 'md' | 'lg';
  icon?: ReactNode;
  trailing?: ReactNode;
  fullWidth?: boolean;
  style?: StyleProp<ViewStyle>;
  textStyle?: StyleProp<TextStyle>;
  disabled?: boolean;
} & Omit<PressableProps, 'children' | 'style'>) {
  const sizing = size === 'lg'
    ? { paddingVertical: 16, paddingHorizontal: 28, fs: 16 }
    : size === 'sm'
    ? { paddingVertical: 8,  paddingHorizontal: 14, fs: 13 }
    : { paddingVertical: 12, paddingHorizontal: 22, fs: 15 };

  return (
    <Pressable
      onPress={onPress}
      disabled={disabled}
      accessibilityRole="button"
      accessibilityLabel={label}
      style={({ pressed, hovered, focused }: any) => [
        styles.btnBase,
        variant === 'primary'   && styles.btnPrimary,
        variant === 'secondary' && styles.btnSecondary,
        variant === 'ghost'     && styles.btnGhost,
        variant === 'ink'       && styles.btnInk,
        variant === 'accent'    && styles.btnAccent,
        { paddingVertical: sizing.paddingVertical, paddingHorizontal: sizing.paddingHorizontal },
        fullWidth && { alignSelf: 'stretch' },
        pressed   && styles.btnPressed,
        hovered   && (variant === 'primary' ? styles.btnPrimaryHover : variant === 'ink' ? styles.btnInkHover : styles.btnSecondaryHover),
        focused   && styles.btnFocused,
        disabled  && styles.btnDisabled,
        style,
      ]}
      {...(rest as any)}
    >
      {icon ? <View style={{ marginInlineEnd: 8 } as any}>{icon}</View> : null}
      <Text
        style={[
          styles.btnText,
          { fontSize: sizing.fs },
          variant === 'primary'   && { color: PAPER },
          variant === 'secondary' && { color: INK },
          variant === 'ghost'     && { color: INK },
          variant === 'ink'       && { color: PAPER },
          variant === 'accent'    && { color: PAPER },
          textStyle,
        ]}
      >
        {label}
      </Text>
      {trailing ? <View style={{ marginInlineStart: 8 } as any}>{trailing}</View> : null}
    </Pressable>
  );
}

// ─── EditorialCard: ink-bordered cream surface ─────────────────────────────
export function EditorialCard({
  children, style, padded = true, tone = 'soft', interactive = false, onPress, ...rest
}: ViewProps & { children?: ReactNode; padded?: boolean; tone?: 'soft' | 'paper' | 'ink'; interactive?: boolean; onPress?: () => void }) {
  const bg = tone === 'paper' ? PAPER : tone === 'ink' ? INK : PAPER_SOFT;
  const fg = tone === 'ink' ? PAPER : INK;
  const inner = (
    <View
      style={[{
        backgroundColor: bg,
        borderWidth: 1,
        borderColor: fg,
        padding: padded ? 24 : 0,
      }, style]}
      {...rest}
    >
      {children}
    </View>
  );
  if (!interactive) return inner;
  return (
    <Pressable
      onPress={onPress}
      style={({ pressed, hovered }: any) => [
        { transform: hovered ? [{ translateY: -2 }] : pressed ? [{ translateY: 1 }] : [],
          ...(IS_WEB ? { transition: 'transform 160ms ease' } as any : {}) },
      ]}
    >
      {inner}
    </Pressable>
  );
}

// ─── EditorialField: ink-bordered input ─────────────────────────────────────
export const EditorialField = forwardRef<TextInput, any>(function EditorialField(
  { label, hint, error, style, inputStyle, ...rest }: any, ref,
) {
  return (
    <View style={style}>
      {label ? (
        <Text style={styles.fieldLabel}>{label}</Text>
      ) : null}
      <TextInput
        ref={ref}
        style={[styles.fieldInput, error ? styles.fieldInputError : null, inputStyle]}
        placeholderTextColor={INK_MUTED}
        {...rest}
      />
      {error ? <Text style={styles.fieldError}>{error}</Text>
        : hint ? <Text style={styles.fieldHint}>{hint}</Text> : null}
    </View>
  );
});

// ─── Tag / Badge ────────────────────────────────────────────────────────────
export function Tag({ label, tone = 'ink', style }: { label: string; tone?: 'ink' | 'accent' | 'forest' | 'gold' | 'paper'; style?: StyleProp<ViewStyle> }) {
  const fg = tone === 'accent' ? ACCENT : tone === 'forest' ? FOREST : tone === 'gold' ? GOLD : tone === 'paper' ? PAPER : INK;
  const bg = tone === 'paper' ? INK : 'transparent';
  return (
    <View style={[{
      borderWidth: 1, borderColor: fg, paddingHorizontal: 10, paddingVertical: 4,
      backgroundColor: bg, alignSelf: 'flex-end',
    }, style]}>
      <Text style={{ fontFamily: MONO, fontSize: 11, color: tone === 'paper' ? PAPER : fg, letterSpacing: 1.2, textTransform: 'uppercase' as any }}>
        {label}
      </Text>
    </View>
  );
}

// ─── Eyebrow: small uppercase label above a headline ───────────────────────
export function Eyebrow({ children, color = ACCENT, style }: { children: ReactNode; color?: string; style?: StyleProp<TextStyle> }) {
  return (
    <View style={{ flexDirection: 'row-reverse', alignItems: 'center', gap: 12 } as any}>
      <View style={{ width: 32, height: 1, backgroundColor: color }} />
      <Text style={[styles.kicker, { color }, style]}>{children}</Text>
    </View>
  );
}

// ─── PageHero: standard editorial article hero ─────────────────────────────
export function PageHero({
  kicker, title, lede, meta, align = 'right', children,
}: {
  kicker?: string; title: ReactNode; lede?: ReactNode; meta?: string; align?: 'right' | 'left' | 'center';
  children?: ReactNode;
}) {
  return (
    <View style={{ paddingTop: 24, paddingBottom: 24 }}>
      {kicker ? <Eyebrow>{kicker}</Eyebrow> : null}
      <View style={{ height: 16 }} />
      <Display align={align} level={1}>{title}</Display>
      {lede ? (
        <>
          <View style={{ height: 16 }} />
          <Text style={{ fontFamily: SERIF, fontSize: 22, lineHeight: 32, color: INK_SOFT, textAlign: align as any, maxWidth: 720 }}>
            {lede}
          </Text>
        </>
      ) : null}
      {meta ? (
        <>
          <View style={{ height: 18 }} />
          <Text style={{ fontFamily: MONO, fontSize: 11, color: INK_MUTED, letterSpacing: 1.4, textTransform: 'uppercase' as any, textAlign: align as any }}>{meta}</Text>
        </>
      ) : null}
      {children ? <View style={{ marginTop: 24 }}>{children}</View> : null}
      <View style={{ height: 32 }} />
      <DoubleRule />
    </View>
  );
}

// ─── Footer: site-wide editorial footer ─────────────────────────────────────
export function EditorialFooter({
  links = [], onNavigate, columns,
}: {
  links?: { label: string; href: string }[];
  onNavigate?: (href: string) => void;
  columns?: { title: string; items: { label: string; href: string }[] }[];
}) {
  const handle = (href: string) => onNavigate?.(href);
  const year = new Date().getFullYear();
  return (
    <View style={{ backgroundColor: INK, marginTop: 64 }}>
      <Section py="xxl" px="md">
        <View style={{ flexDirection: 'row-reverse', alignItems: 'flex-start', justifyContent: 'space-between', flexWrap: 'wrap', gap: 32 } as any}>
          <View style={{ minWidth: 240, flexShrink: 1 }}>
            <Text style={{ fontFamily: SERIF_BLACK, fontSize: 44, color: PAPER, letterSpacing: -1 }}>Kursi</Text>
            <View style={{ height: 8 }} />
            <Text style={{ fontFamily: SERIF, fontSize: 18, color: PAPER_SOFT, lineHeight: 26, maxWidth: 320, textAlign: 'right' }}>
              מנוע התאמת קורסים מבוסס AI לשוק הישראלי.
            </Text>
            <View style={{ height: 20 }} />
            <Text style={{ fontFamily: MONO, fontSize: 11, color: INK_FAINT, letterSpacing: 1.5, textTransform: 'uppercase' as any, textAlign: 'right' }}>
              EST. {year} · TEL AVIV · ISRAEL
            </Text>
          </View>

          {columns?.map((col, ci) => (
            <View key={ci} style={{ minWidth: 160 }}>
              <Text style={{ fontFamily: MONO, fontSize: 11, color: ACCENT, letterSpacing: 1.6, textTransform: 'uppercase' as any, textAlign: 'right', marginBottom: 14 }}>{col.title}</Text>
              {col.items.map((it, ii) => (
                <Pressable key={ii} onPress={() => handle(it.href)} style={{ paddingVertical: 6 }}>
                  <Text style={{ fontFamily: SANS_MED, fontSize: 15, color: PAPER, textAlign: 'right' }}>{it.label}</Text>
                </Pressable>
              ))}
            </View>
          ))}

          {!columns && links.length > 0 ? (
            <View style={{ minWidth: 200 }}>
              <Text style={{ fontFamily: MONO, fontSize: 11, color: ACCENT, letterSpacing: 1.6, textTransform: 'uppercase' as any, textAlign: 'right', marginBottom: 14 }}>ניווט</Text>
              {links.map((l, i) => (
                <Pressable key={i} onPress={() => handle(l.href)} style={{ paddingVertical: 6 }}>
                  <Text style={{ fontFamily: SANS_MED, fontSize: 15, color: PAPER, textAlign: 'right' }}>{l.label}</Text>
                </Pressable>
              ))}
            </View>
          ) : null}
        </View>

        <View style={{ height: 48 }} />
        <View style={{ height: 1, backgroundColor: INK_FAINT, opacity: 0.4 }} />
        <View style={{ height: 24 }} />
        <View style={{ flexDirection: 'row-reverse', flexWrap: 'wrap', justifyContent: 'space-between', alignItems: 'center', gap: 12 } as any}>
          <Text style={{ fontFamily: MONO, fontSize: 11, color: INK_FAINT, letterSpacing: 1.4 }}>
            © {year} KURSI · ALL RIGHTS RESERVED
          </Text>
          <View style={{ flexDirection: 'row-reverse', gap: 18 } as any}>
            <Pressable onPress={() => handle('/legal/terms')}>
              <Text style={{ fontFamily: MONO, fontSize: 11, color: PAPER_SOFT, letterSpacing: 1.4, textTransform: 'uppercase' as any }}>תנאי שימוש</Text>
            </Pressable>
            <Pressable onPress={() => handle('/legal/privacy')}>
              <Text style={{ fontFamily: MONO, fontSize: 11, color: PAPER_SOFT, letterSpacing: 1.4, textTransform: 'uppercase' as any }}>פרטיות</Text>
            </Pressable>
            <Pressable onPress={() => handle('/legal/accessibility')}>
              <Text style={{ fontFamily: MONO, fontSize: 11, color: PAPER_SOFT, letterSpacing: 1.4, textTransform: 'uppercase' as any }}>נגישות</Text>
            </Pressable>
          </View>
        </View>
      </Section>
    </View>
  );
}

// ─── Styles ─────────────────────────────────────────────────────────────────
const styles = StyleSheet.create({
  // Type
  display: {
    fontFamily: SERIF_BLACK,
    fontSize: theme.type.display,
    lineHeight: theme.type.display * 1.02,
    letterSpacing: -1.5,
    color: INK,
  },
  headline: {
    fontFamily: SERIF_BOLD,
    fontSize: theme.type.headline,
    lineHeight: theme.type.headline * 1.05,
    letterSpacing: -0.8,
    color: INK,
  },
  title: {
    fontFamily: SERIF_BOLD,
    fontSize: theme.type.title,
    lineHeight: theme.type.title * 1.15,
    letterSpacing: -0.4,
    color: INK,
  },
  sectionTitle: {
    fontFamily: SERIF_BOLD,
    fontSize: theme.type.section,
    lineHeight: theme.type.section * 1.2,
    letterSpacing: -0.2,
    color: INK,
  },
  body: { fontFamily: SANS, fontSize: 17, lineHeight: 28, color: INK_SOFT },
  bodyLg: { fontFamily: SANS, fontSize: 19, lineHeight: 31, color: INK_SOFT },
  small: { fontFamily: SANS, fontSize: 14, lineHeight: 22, color: INK_MUTED },
  caption: { fontFamily: SANS, fontSize: 12, lineHeight: 18, color: INK_MUTED },
  kicker: {
    fontFamily: MONO, fontSize: 11, letterSpacing: 2,
    textTransform: 'uppercase' as any, color: ACCENT,
  },

  // Buttons
  btnBase: {
    flexDirection: 'row-reverse',
    alignItems: 'center',
    justifyContent: 'center',
    borderWidth: 1,
    borderColor: INK,
    ...(IS_WEB ? { transition: 'all 180ms ease', cursor: 'pointer' } as any : {}),
  },
  btnPrimary:   { backgroundColor: INK },
  btnPrimaryHover: { backgroundColor: ACCENT, borderColor: ACCENT },
  btnSecondary: { backgroundColor: 'transparent' },
  btnSecondaryHover: { backgroundColor: INK },
  btnGhost:     { borderColor: 'transparent', backgroundColor: 'transparent' },
  btnInk:       { backgroundColor: INK, borderColor: INK },
  btnInkHover:  { backgroundColor: ACCENT_DEEP, borderColor: ACCENT_DEEP },
  btnAccent:    { backgroundColor: ACCENT, borderColor: ACCENT },
  btnPressed:   { opacity: 0.85 },
  btnFocused:   { ...(IS_WEB ? { outlineWidth: 2, outlineColor: ACCENT, outlineOffset: 2, outlineStyle: 'solid' } as any : {}) },
  btnDisabled:  { opacity: 0.4 },
  btnText: {
    fontFamily: SANS_BOLD,
    letterSpacing: 0.3,
    color: PAPER,
  },

  // Field
  fieldLabel: {
    fontFamily: MONO,
    fontSize: 11,
    color: INK_MUTED,
    letterSpacing: 1.6,
    textTransform: 'uppercase' as any,
    marginBottom: 8,
    textAlign: 'right',
  },
  fieldInput: {
    borderWidth: 0,
    borderBottomWidth: 1.5,
    borderColor: INK,
    backgroundColor: 'transparent',
    paddingVertical: 12,
    paddingHorizontal: 0,
    fontFamily: SANS_MED,
    fontSize: 18,
    color: INK,
    textAlign: 'right',
    ...(IS_WEB ? { outlineStyle: 'none', direction: 'rtl' } as any : {}),
  },
  fieldInputError: { borderColor: CRIMSON },
  fieldHint: { fontFamily: SANS, fontSize: 12, color: INK_MUTED, marginTop: 6, textAlign: 'right' },
  fieldError: { fontFamily: SANS_MED, fontSize: 12, color: CRIMSON, marginTop: 6, textAlign: 'right' },
});

// ─── Hook: responsive helpers ───────────────────────────────────────────────
export function useEditorialBreakpoint() {
  const { width } = useWindowDimensions();
  return useMemo(() => ({
    width,
    isMobile: width < 720,
    isTablet: width >= 720 && width < 1024,
    isDesktop: width >= 1024,
  }), [width]);
}

// Re-export tokens for convenience
export const TOKENS = { INK, INK_SOFT, INK_MUTED, INK_FAINT, PAPER, PAPER_SOFT, ACCENT, ACCENT_DEEP, RULE, CRIMSON, FOREST, GOLD, SERIF, SERIF_BOLD, SERIF_BLACK, SERIF_MED, SANS, SANS_MED, SANS_SEMI, SANS_BOLD, MONO };
